import { BehaviorSubject, combineLatest, distinctUntilKeyChanged, filter, map, Observable, shareReplay, Subject, switchMap, take } from 'rxjs';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BusinessThemeService } from '@aston/themes';
import { inject, Injectable } from '@angular/core';

import { objectToStyleAttribute, systemMessage } from '../functions/utils.ai';
import { ChatBotResponse } from '../models/ai-response.model';
import { AI_API_ROOT, AI_CURRENT_USER } from '../di';

import { AIChatboxSuggestionsService } from './ai-chatbox-suggestions.service';
import { OboeClient } from './http-stream.service';

export const AI_CHAT_CHARACTER_LIMIT = 500 - 100; // for app context
export const AI_CHAT_EMPTY_RESPONSE_TIMEOUT = 100; // when AVA is not responding
export const AI_CHAT_PARTIAL_RESPONSE_TIMEOUT = 8000; // when AVA is hanging

@Injectable({ providedIn: 'root' })
export class AIChatboxService {
	sugg = inject(AIChatboxSuggestionsService)
	theme = inject(BusinessThemeService)
	i18n = inject(TranslateService)
	http = inject(OboeClient)
	api$ = inject(AI_API_ROOT)

	protected _loading$ = new BehaviorSubject(false)
	public loading$ = this._loading$.asObservable()
	
	protected _onUserMessage$ = new Subject<ChatBotResponse>()
	public onUserMessage$ = this._onUserMessage$.asObservable()
	
	protected _onResponse$ = new Subject<ChatBotResponse>()
	public onResponse$ = this._onResponse$.asObservable()

	protected _onFinalResponse$ = new Subject<ChatBotResponse>()
	public onFinalResponse$ = this._onFinalResponse$.asObservable()
	
	protected restart$ = new BehaviorSubject(null)
	public onRestart$ = this.restart$.asObservable()

	protected currentThread$ = new BehaviorSubject<{ id?: string, lastMessageId?: string }>({})
	currentUser$ = inject(AI_CURRENT_USER)

	hello$ = combineLatest([
		this.currentUser$.pipe(filter(Boolean), distinctUntilKeyChanged('id')),
		this.restart$,
	]).pipe(
		map(([user]) => user),
		switchMap(user => this.i18n.stream('Home.Page.Hello', { name: user.fullName.firstName })),
		shareReplay()
	);

	intro$ = combineLatest([
		this.hello$,
		this.i18n.stream('App.AIChat.Intro'),
		this.theme.getThemed('aiChat')
	]).pipe(
		map(([hello, intro, themeStyle]) => [`
			<div style="${objectToStyleAttribute(themeStyle.intro)}">
				<div style="margin: 2em 0; font-weight: bold; font-size: 1.5em;">${hello}</div>
				<p style="margin-bottom: 1em; white-space: preserve;">${intro}</p>
			</div>
		`])
	)

	connect$ = this.api$.pipe(
		map(_ => ({
			stream: {
				htmlWrappers: {
					ai: `
						<div>
							<span class="stream-wrapper"></span>
							<div class="stream-actions" style="text-align:right;">
								<span class="stream-actions-copy custom-button-container-default" title="${this.i18n.instant('Actions.Copy')}">copy</span>
							</div>
						</div>
					`,
				},
			},
			handler: (body: any, signals: any) => {
				const question = body.messages[0].text;

				if (question.length > AI_CHAT_CHARACTER_LIMIT) {
					signals.onResponse(systemMessage({
						text: this.i18n.instant('Forms.FieldErrorMessages.MaxLengthPlural',
						{ count: AI_CHAT_CHARACTER_LIMIT })
					}))
					signals.onClose();
					return Promise.resolve()
				}

				body = this.hydrateRequest(body)
				this._loading$.next(true)

				const close = () => {
					this._loading$.next(false)
					signals.onClose()
				}

				let partialResponseTimeout: any = null;
				const startClosingTimer = () => {
					console.debug(`[ai-chat] start partial response timeout timer`);
					partialResponseTimeout = setTimeout(() => close(), AI_CHAT_PARTIAL_RESPONSE_TIMEOUT);
				}

				let lastResp: ChatBotResponse | undefined;
				let accumulatedText = '';
				this.postMessage(body).subscribe({
					next: (event: HttpEvent<string>) => {
						clearTimeout(partialResponseTimeout);

						if (event.type === HttpEventType.Response) {
							if (!event.body?.slice) return;
							const resp = (event.body as unknown as ChatBotResponse[]).slice(-1).pop()
							if (!resp) {
								console.debug(`[ai-chat] missing response`)
								startClosingTimer();
								return;
							}
							this.currentThread$.next({
								id: resp.threadId,
								lastMessageId: resp.contextId,
							})
							if (resp.text) {
								lastResp = resp
								accumulatedText += resp.text
								this.onResponse(signals, resp)
								startClosingTimer();
							} else {
								setTimeout(() => close(), AI_CHAT_EMPTY_RESPONSE_TIMEOUT)
							}
						}
					},
					complete: () => {
						console.debug(`[ai-chat] chat response completed`)
						if (lastResp && accumulatedText) this._onFinalResponse$.next({ ...lastResp, text: accumulatedText })
						setTimeout(() => close(), AI_CHAT_EMPTY_RESPONSE_TIMEOUT)
					},
					error: (error) => {
						console.debug(`[ai-chat] chat response error`, error)
						signals.onResponse({ error })
					}
				});
			}
		})
	))

	protected onResponse(signals: { onResponse: CallableFunction }, response: ChatBotResponse) {
		signals.onResponse({ text: response.text })
		this._onResponse$.next(response)
	}

	protected postMessage(body: any): Observable<HttpEvent<string>> {
		const message = {
			contextId: this.currentThread$.value?.lastMessageId,
			threadId: this.currentThread$.value?.id,
			question: body.messages[0].text,
			languageCode: this.i18n.currentLang,
		}
		this._onUserMessage$.next(message)
		return  this.api$.pipe(
			take(1),
			switchMap(url => this.http.post(
				`${url}/api/AiChat/chatstreaming`, message,
				{
					observe: "events",
					responseType: "text",
					reportProgress: true,
				}
			))
		)
	}

	startNewThread() {
		this.currentThread$.next({})
		this.restart$.next(null)
	}

	resumeThread(message: ChatBotResponse) {
		this.currentThread$.next({
			lastMessageId: message.contextId,
			id: message.threadId,
		})
	}

	// Prefix requests with the current app and page title
	protected hydrateRequest(body: any) {
		const message = body?.messages?.[0]
		if (!message?.text) return body;
		const from = this.i18n.instant(`App.AIChat.FromApp`);
		message.text = `${from}, ${message.text}`
		return body;
	}
}
